import { OwcListItem, OwcCheckbox, OwcList, OwcTypography } from '@one/react';
import React from 'react';
import '../../styles.scss';
import DelayedTooltip from './DelayedTooltip';
import { hasItemWithNoSublist } from './shared/SubListHelper.js';

const emptySublist = "No sublist"

/**
 * Renders a reference list as as selection list.
 *
 * @copyright Roche 2024
 * @author Geoffroy Maillol
 */
class ReferenceListSelector extends React.Component {
  /**
   * Constructor 
   * 
   * @param props The properties passed
   */
  constructor(props) {
      super(props);
      // Initialise the sublist list to be expanded 
      const sublistExpandedState = Object.fromEntries(
        this.getUniqueSublists().map(sublist => [sublist, true]));
      this.state = {
        showDeprecated: props.displayDeprecatedItems,
        sublistExpandedState: sublistExpandedState,
      };
  }

  getUniqueSublists() {
    // Drop null/undefined/blank entries: a malformed sublist array (e.g.
    // ["x", null] or [""]) leaks those values through flatMap and OWC v7 renders
    // each as a stray empty expandable group.
    const sublists = [...new Set(this.props.refListData.flatMap(item => item.sublist))]
        .filter(sublist => sublist && sublist.trim() !== "");
    // Only surface the pseudo "No sublist" group when at least one item actually
    // lacks a sublist. OWC v7 renders an empty expandable header (v6 hid it), so
    // appending it unconditionally showed a stray "No sublist" entry.
    if (hasItemWithNoSublist(this.props.refListData)) {
      sublists.push(emptySublist);
    }
    return sublists;
  }

  handleExpandedChange(subListItem, newValue) {
    const sublistExpandedState = this.state.sublistExpandedState;
    sublistExpandedState[subListItem] = newValue;
    this.setState({sublistExpandedState: sublistExpandedState});
  }

  sublistIsEmpty(item, subListItem) {
    return subListItem === emptySublist && (!Array.isArray(item.sublist) || item.sublist.length === 0);
  }

  itemInSublists(item, subListItem) {
    return item.sublist && item.sublist.includes(subListItem);
  }

  renderItem(item) {
    if (this.isItemVisible(item)) {
      return (
        <>
        <OwcListItem key={item.refListId} id={item.refListId}
            size={this.props.size}
            value={item.refListId} >
          {item.description}{item.isActive? "" : " (Deprecated)"}
        </OwcListItem>
        {(this.props.listItemTooltips===true && item.comment!==null && item.comment.length > 0)?
         <DelayedTooltip key={`${item.refListId}tooltip`} id={`${item.refListId}tooltip`} anchor={item.refListId} offset={[0,-1]}>
          {item.comment}
         </DelayedTooltip>
        :
         <></>
        }
        </>
      );
    }
  }

  isItemVisible(item) {
    return item.isActive || (!item.isActive && this.state.showDeprecated);
  }

  aggregateBySublist() {
    return this.getUniqueSublists()
      .map(subListItem => {
        const itemsInSublist = this.props.refListData
          .filter(item => this.itemInSublists(item, subListItem) || this.sublistIsEmpty(item, subListItem));
        return { subListItem, itemsInSublist };
      })
      // OWC v6 hid an expandable group whose children list was empty; OWC v7
      // renders the (empty) header instead. Skip any group with no visible
      // items so empty sublists - including a "No sublist" group whose members
      // are all filtered out - don't appear as stray blank rows.
      .filter(({ itemsInSublist }) => itemsInSublist.some(item => this.isItemVisible(item)))
      .map(({ subListItem, itemsInSublist }) =>
        <OwcListItem key={"SublistListItem" + subListItem} expanded={this.state.sublistExpandedState[subListItem]}
            onExpandedChange={(ev) => this.handleExpandedChange(subListItem, ev.detail)}>
          {subListItem}
          <OwcList key={"ItemInSubList" + subListItem} slot="children">
            {itemsInSublist.map(item => this.renderItem(item))}
          </OwcList>
        </OwcListItem>
      );
  }

  /**
   * Renders the controls
   * @returns The JSX of the controls
   */
  render() {
    if (this.props.refListData) {
      return (
        <div style={{ display:"flex", flexDirection:"column" }}>
          {this.props.title.length>0?
          <OwcTypography variant="title6">
            {this.props.title}
          </OwcTypography>
          :
          ""
          }
          {
            this.props.displayDeprecatedToggle === true
            ? <OwcCheckbox onValueChange={(ev) => this.setState({showDeprecated: ev.detail})}>
                Display deprecated items
              </OwcCheckbox>
            : ""
          }
          
          <OwcList multiple showSelectorControl selected={this.props.selectedItems}
              onSelectedChange={(ev) => this.props.onSelectedChange(ev.detail)}>
            {this.props.aggregateSublist
              ? this.aggregateBySublist()
              : this.props.refListData.map(item => this.renderItem(item))
            }
          </OwcList>
        </div>
      );
    }
  }
}

ReferenceListSelector.defaultProps = {
  size: 'small',
  displayDeprecatedToggle: false,
  displayDeprecatedItems: false,
  title: "",
  aggregateSublist: false,
  listItemTooltips: true
}

export default ReferenceListSelector
